import React, { useState } from 'react';
import { CSVLink } from 'react-csv';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import './TauriSummaryVisual.css';

const TauriSummaryVisual = ({ chartConfig, chartData, chartPalettes }) => {
    const [exportToggleOpen, setExportToggleOpen] = useState(false);

    if (!chartData || Object.keys(chartData).length === 0) {
        return <div className="no-records-container">There are no records to display</div>;
    }

    const exportToggle = () => setExportToggleOpen(!exportToggleOpen);

    const handleCopyToClipboard = () => {
        const formattedData = chartConfig?.binding?.data?.displayTarget
            ? chartData.map(({ ActualSales, TargetSales, Performance, LYSales, Growth }) => ({
                ActualSales,
                TargetSales,
                Performance,
                LYSales,
                Growth,
            }))
            : chartData.map(({ ActualSales, LYSales, Growth }) => ({
                ActualSales,
                LYSales,
                Growth,
            }));

        const header = chartConfig?.binding?.data?.displayTarget
            ? ["ActualSales", "TargetSales", "Performance", "LYSales", "Growth"]
            : ["ActualSales", "LYSales", "Growth"];

        const dataString = [header.join("\t"), ...formattedData.map(row => Object.values(row).join("\t"))].join("\n");

        navigator.clipboard.writeText(dataString).catch(err => {
            console.error('Failed to copy data:', err);
        });
    };

    const formatNumber = (num) => {
        if (num >= 1e9) {
            return (num / 1e9).toFixed(2) + 'B';
        } else if (num >= 1e6) {
            return (num / 1e6).toFixed(2) + 'M';
        } else if (num >= 1e3) {
            return (num / 1e3).toFixed(2) + 'K';
        } else {
            return num.toString();
        }
    };

    const exportData = chartConfig?.binding?.data?.displayTarget
        ? chartData.map(({ ActualSales, TargetSales, Performance, LYSales, Growth, ActualSalesYTD, TargetSalesYTD, PerformanceYTD, LYSalesYTD, GrowthYTD }) => ({
            ActualSales,
            TargetSales,
            Performance,
            LYSales,
            Growth,
            ActualSalesYTD,
            TargetSalesYTD,
            PerformanceYTD,
            LYSalesYTD,
            GrowthYTD
        }))
        : chartData.map(({ ActualSales, LYSales, Growth, ActualSalesYTD, LYSalesYTD, GrowthYTD }) => ({
            ActualSales,
            LYSales,
            Growth,
            ActualSalesYTD,
            LYSalesYTD,
            GrowthYTD
        }));

    const { Performance, MonthToGo, ActualSales, TargetSales, LYSales, Growth, ActualSalesYTD, TargetSalesYTD, PerformanceYTD, LYSalesYTD, GrowthYTD } = chartData[0];

    return (
        <div className="chart-content">
            <div className='mt-2'>
                <Dropdown isOpen={exportToggleOpen} toggle={exportToggle} className="taurisummary-toolbar" style={{ position: 'absolute', right: '0' }}>
                    <DropdownToggle tag="span">
                        <img
                            src={process.env.PUBLIC_URL + "/Images/charm--menu-hamburger.svg"}
                            className='dropdown-img'
                            alt="Icon"
                        />
                    </DropdownToggle>

                    <DropdownMenu right>
                        <DropdownItem onClick={handleCopyToClipboard}>
                            Copy to clipboard
                        </DropdownItem>
                        <DropdownItem>
                            <CSVLink data={exportData} filename={`${'Sales Performance Summary'}.csv`}>
                                Download CSV
                            </CSVLink>
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
                <div className='row mb-5'>
                    <div className="col-2">
                        <p className='m-0' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>Sales (YTD)</p>
                        <p className='fw-bold m-0' style={{ fontFamily: 'Lato-Light', fontSize: '1.6875rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ActualSalesYTD !== undefined ? formatNumber(ActualSalesYTD) : 0}
                        </p>
                    </div>
                    <div className="col-2">
                        <p className='m-0' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>Target (YTD)</p>
                        <p className='fw-bold m-0' style={{ fontFamily: 'Lato-Light', fontSize: '1.6875rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {TargetSalesYTD !== undefined ? formatNumber(TargetSalesYTD) : 0}
                        </p>
                    </div>
                    <div className="col-2">
                        <p className='m-0' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>LY Sales (YTD)</p>
                        <p className='fw-bold m-0' style={{ fontFamily: 'Lato-Light', fontSize: '1.6875rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {LYSalesYTD !== undefined ? formatNumber(LYSalesYTD) : 0}
                        </p>
                    </div>
                    <div className="col-2">
                        <p className='m-0' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>Growth (YTD)</p>
                        <p className='fw-bold m-0'
                            style={{
                                fontFamily: 'Lato-Light',
                                fontSize: '1.6875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: GrowthYTD < 0 ? 'red' : chartPalettes[0].ranges[0]
                            }}>
                            {GrowthYTD !== undefined ? GrowthYTD.toLocaleString(undefined, { maximumFractionDigits: 2 }) + '%' : 0}
                        </p>
                    </div>
                    <div className="col-2 ">
                        {
                            chartConfig?.binding?.data?.displayTarget && (
                                <>
                                    <p className='m-0' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>Performance(YTD)</p>
                                    <p className='fw-bold m-0'
                                        style={{
                                            fontFamily: 'Lato-Light',
                                            fontSize: '1.6875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            color: PerformanceYTD < 0 ? 'red' : chartPalettes[0].ranges[0]
                                        }}>
                                        {PerformanceYTD !== undefined ? PerformanceYTD.toLocaleString(undefined, { maximumFractionDigits: 2 }) + '%' : 0}
                                    </p>
                                </>
                            )
                        }

                    </div>


                </div>
                <div className="row ">
                    <div className="col-2">
                        <p className='m-0' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>Sales (MTD)</p>
                        <p className='fw-bold m-0' style={{ fontFamily: 'Lato-Light', fontSize: '1.6875rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ActualSales !== undefined ? formatNumber(ActualSales) : 0}
                        </p>
                    </div>

                    <div className="col-2">
                        <p className='m-0' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>Target (MTD)</p>
                        <p className='fw-bold m-0' style={{ fontFamily: 'Lato-Light', fontSize: '1.6875rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {TargetSales !== undefined ? formatNumber(TargetSales) : 0}
                        </p>
                    </div>
                    <div className="col-2">
                        <p className='m-0' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>LY Sales (MTD)</p>
                        <p className='fw-bold m-0' style={{ fontFamily: 'Lato-Light', fontSize: '1.6875rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {LYSales !== undefined ? formatNumber(LYSales) : 0}
                        </p>
                    </div>
                    
                    <div className="col-2">
                        <p className='m-0' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>Growth (MTD)</p>
                        <p className='fw-bold m-0'
                            style={{
                                fontFamily: 'Lato-Light',
                                fontSize: '1.6875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: GrowthYTD < 0 ? 'red' : chartPalettes[0].ranges[0]
                            }}>
                            {Growth !== undefined ? Growth.toLocaleString(undefined, { maximumFractionDigits: 2 }) + '%' : 0}
                        </p>
                    </div>
                    <div className="col-2 ">
                        <>
                            <p className='m-0' style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>Performance(MTD)</p>
                            <p className='fw-bold m-0'
                                style={{
                                    fontFamily: 'Lato-Light',
                                    fontSize: '1.6875rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: Performance < 0 ? 'red' : chartPalettes[0].ranges[0]
                                }}>
                                {Performance !== undefined ? Performance.toLocaleString(undefined, { maximumFractionDigits: 2 }) + '%' : 0}
                            </p>
                        </>

                    </div>

                </div>



            </div>
        </div>
    );
};

export default TauriSummaryVisual;
